<template>
  <div class="modal fade" id="project_details" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <div class="d-flex flex-column">
            <h5 class="modal-title mb-1" v-if="project">
              {{ project.name }}
              <span class="text-muted fs-12 ms-2">
                ({{ project.project_code || "No code" }})
              </span>
            </h5>
            <div
              v-if="project"
              class="d-flex flex-wrap align-items-center mt-1"
            >
              <!-- Status -->
              <span
                class="badge badge-xs me-2"
                :class="
                  project.status === 'Active' ? 'badge-success' : 'badge-danger'
                "
              >
                <i class="ti ti-point-filled me-1"></i>{{ project.status }}
              </span>
              <!-- Priority -->
              <span class="d-inline-flex align-items-center">
                <span
                  class="rounded-circle d-flex justify-content-center align-items-center me-1"
                  :class="{
                    'bg-transparent-danger': project.priority === 'High',
                    'bg-transparent-warning': project.priority === 'Medium',
                    'bg-transparent-success': project.priority === 'Low',
                  }"
                >
                  <i
                    class="ti ti-point-filled"
                    :class="{
                      'text-danger': project.priority === 'High',
                      'text-warning': project.priority === 'Medium',
                      'text-success': project.priority === 'Low',
                    }"
                  ></i>
                </span>
                <small class="text-muted"
                  >Priority: {{ project.priority }}</small
                >
              </span>
            </div>
          </div>

          <button
            type="button"
            class="btn-close"
            @click="close"
            aria-label="Close"
          ></button>
        </div>

        <!-- LOADING STATE -->
        <div v-if="loading" class="modal-body text-center py-5">
          <span class="spinner-border mb-3"></span>
          <p class="mb-0">Loading project details...</p>
        </div>

        <!-- CONTENT -->
        <div v-else-if="project" class="modal-body pt-3 pb-4">
          <div class="row">
            <!-- LEFT COLUMN: description & summary -->
            <div class="col-lg-8">
              <!-- Description -->
              <div class="card mb-3">
                <div class="card-header border-0 pb-2">
                  <h6 class="mb-0">Overview</h6>
                </div>
                <div class="card-body pt-2">
                  <div
                    v-if="project.description"
                    class="project-description text-muted"
                    v-html="project.description"
                  ></div>
                  <p v-else class="text-muted mb-0">
                    No description provided for this project.
                  </p>
                </div>
              </div>

              <!-- Tasks summary (counts from backend) -->
              <div class="card">
                <div
                  class="card-header border-0 pb-2 d-flex justify-content-between align-items-center"
                >
                  <h6 class="mb-0">Tasks Summary</h6>
                  <span
                    v-if="project.total_tasks != null"
                    class="fs-12 text-muted"
                  >
                    {{ project.completed_tasks || 0 }} of
                    {{ project.total_tasks }} completed
                  </span>
                </div>
                <div class="card-body pt-2">
                  <div v-if="project.total_tasks != null">
                    <div class="d-flex align-items-center mb-2">
                      <div class="flex-grow-1 me-3">
                        <div class="progress" style="height: 6px">
                          <div
                            class="progress-bar"
                            role="progressbar"
                            :style="{ width: progressPercent + '%' }"
                            :aria-valuenow="progressPercent"
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                      <span class="fs-12 text-muted">
                        {{ progressPercent }}%
                      </span>
                    </div>
                    <p class="text-muted fs-12 mb-0">
                      Tracked from tasks linked to this project.
                    </p>
                  </div>
                  <div v-else class="text-muted fs-12">
                    No task data found yet for this project.
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT COLUMN: meta info -->
            <div class="col-lg-4">
              <!-- Meta card -->
              <div class="card mb-3">
                <div class="card-header border-0 pb-2">
                  <h6 class="mb-0">Project Info</h6>
                </div>
                <div class="card-body pt-2">
                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted fs-12">Client</span>
                    <span class="fw-medium fs-12">
                      {{ project.client_name || "N/A" }}
                    </span>
                  </div>

                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted fs-12">Value</span>
                    <span class="fw-medium fs-12">
                      {{ project.value ? project.value.toLocaleString() : "—" }}
                    </span>
                  </div>

                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted fs-12">Start Date</span>
                    <span class="fw-medium fs-12">
                      {{ formatDate(project.start_date) }}
                    </span>
                  </div>

                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted fs-12">End Date</span>
                    <span class="fw-medium fs-12">
                      {{ formatDate(project.end_date) }}
                    </span>
                  </div>

                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted fs-12">Created</span>
                    <span class="fw-medium fs-12">
                      {{ formatDate(project.created_at) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- People -->
              <div class="card">
                <div class="card-header border-0 pb-2">
                  <h6 class="mb-0">People</h6>
                </div>
                <div class="card-body pt-2">
                  <!-- Leader -->
                  <div class="d-flex align-items-center mb-3">
                    <span
                      class="avatar avatar-sm avatar-rounded border flex-shrink-0"
                    >
                      <img
                        v-if="project.leader_avatar"
                        :src="project.leader_avatar"
                        class="img-fluid"
                        alt="leader"
                      />
                      <img
                        v-else
                        :src="require('@/assets/img/users/user-01.jpg')"
                        class="img-fluid"
                        alt="leader"
                      />
                    </span>
                    <div class="ms-2">
                      <p class="mb-0 fs-12 fw-medium">
                        {{ project.leader_name || "Unassigned" }}
                      </p>
                      <span class="text-muted fs-12">Project Leader</span>
                    </div>
                  </div>

                  <!-- Team -->
                  <div>
                    <p class="text-muted fs-12 mb-1">Team Members</p>
                    <div class="avatar-list-stacked avatar-group-sm mb-1">
                      <span
                        v-for="(av, i) in (project.team_avatars || []).slice(
                          0,
                          3
                        )"
                        :key="i"
                        class="avatar avatar-rounded"
                      >
                        <img
                          class="border border-white"
                          :src="
                            av || require('@/assets/img/profiles/avatar-02.jpg')
                          "
                          alt="img"
                        />
                      </span>
                      <a
                        v-if="(project.extra_team_count || 0) > 0"
                        class="avatar bg-primary avatar-rounded text-fixed-white fs-12 fw-medium"
                        href="javascript:void(0);"
                      >
                        +{{ project.extra_team_count }}
                      </a>
                    </div>
                    <p
                      v-if="
                        !project.team_avatars || !project.team_avatars.length
                      "
                      class="text-muted fs-12 mb-0"
                    >
                      No members assigned to this project.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- FOOTER -->
        <div class="modal-footer border-0 pt-0">
          <button type="button" class="btn btn-light" @click="close">
            Close
          </button>
          <router-link
            v-if="project"
            :to="`/projects/projects-details/${project.id}`"
            class="btn btn-primary"
            @click="close"
          >
            Open full page
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useProjectStore } from "@/stores/projectStore";
import { Modal } from "bootstrap";

const store = useProjectStore();
const project = ref(null);
const loading = ref(false);

const progressPercent = computed(() => {
  if (!project.value || !project.value.total_tasks) return 0;
  const done = project.value.completed_tasks || 0;
  const total = project.value.total_tasks || 1;
  const pct = Math.round((done * 100) / total);
  return isNaN(pct) ? 0 : pct;
});

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v).slice(0, 10);
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

async function open(id) {
  const el = document.getElementById("project_details");
  if (!el) return;

  let instance = Modal.getInstance(el);
  if (!instance) {
    instance = new Modal(el, { backdrop: "static", keyboard: true });
  }

  loading.value = true;
  project.value = null;
  instance.show();

  try {
    const data = await store.ensureOne(id);
    project.value = data;
  } catch (e) {
    // if fetch fails, hide the modal
    instance.hide();
    throw e;
  } finally {
    loading.value = false;
  }
}

function close() {
  const el = document.getElementById("project_details");
  if (!el) return;
  const instance = Modal.getInstance(el);
  instance?.hide();
}

defineExpose({ open, close });
</script>

<style scoped>
.project-description :deep(p) {
  margin-bottom: 0.5rem;
}
.project-description :deep(ul),
.project-description :deep(ol) {
  padding-left: 1.25rem;
}
</style>
